import { createContext, type FC, type ReactNode, useContext } from 'react';

export type ServerVarsContextType = {
    isBot?: boolean; // should be set for any bot
    isSsrOnlyBot?: boolean; // should be set only for SSR-only bot
    isDesktop?: boolean | null;
    isMobile?: boolean | null;
    areDisplayAdsEnabled?: boolean | null;
    isTablet?: boolean | null;
    clientConf?: { [key: string]: string | number | undefined } | null;
    localeOrigin?: string | null;
    isSearchQueryApplied?: boolean | null;
    locale?: string | null;
    originalUrl?: string | null;
    isTradeQuickFiltersVariant?: boolean;
    isPdpMwLayoutShiftFixVariant?: boolean;
    isTableSizeFilterVariant?: boolean;
    urgencySignalsMwVariant?: string;
    isContentVisibilitySbVariant?: boolean;
    isPdpRespPreviewPage?: boolean;
    isBannerAdsAbTestVariant?: boolean;
    isShipsFromSBTileVariant?: boolean;
    pdpMwUxRedesignVariant?: string | null;
    isItemTileRedesignTestVariant?: boolean;
    isPdpSellerReviewsVariant?: boolean;
    isPdpEstimateSimilarSoldTestVariant?: boolean;
    isPersonalizedCollectionTestVariant?: boolean;
    isItemFavoritingVariant?: boolean;
    isItemFavoritingFeb2026Variant?: boolean;
    isItemFavoritingFeb2026Control?: boolean;
    isHolidayGiftCollectionPage?: boolean;
    isPdpBottomNewSlModuleTestVariant?: boolean;
};

export const ServerVarsContext = createContext<ServerVarsContextType>({
    isBot: false,
    isSsrOnlyBot: false,
    isDesktop: null,
    isMobile: null,
    areDisplayAdsEnabled: null,
    isTablet: null,
    clientConf: {},
    localeOrigin: null,
    locale: null,
    originalUrl: null,
    isSearchQueryApplied: null,
    isTradeQuickFiltersVariant: false,
    isPdpMwLayoutShiftFixVariant: false,
    isTableSizeFilterVariant: false,
    urgencySignalsMwVariant: undefined, // if test is off, "urgencySignalsMwVariant" should not have value
    isContentVisibilitySbVariant: false,
    isPdpRespPreviewPage: false,
    isBannerAdsAbTestVariant: false,
    isShipsFromSBTileVariant: false,
    pdpMwUxRedesignVariant: null,
    isItemTileRedesignTestVariant: false,
    isPdpSellerReviewsVariant: false,
    isPdpEstimateSimilarSoldTestVariant: false,
    isPersonalizedCollectionTestVariant: false,
    isItemFavoritingVariant: false,
    isItemFavoritingFeb2026Variant: false,
    isItemFavoritingFeb2026Control: false,
    isHolidayGiftCollectionPage: false,
    isPdpBottomNewSlModuleTestVariant: false,
});

export const useServerVarsContext = (): ServerVarsContextType =>
    useContext<ServerVarsContextType>(ServerVarsContext);

export const ServerVarsContextConsumer = ServerVarsContext.Consumer;

export const ServerVarsContextProvider: FC<ServerVarsContextType & { children: ReactNode }> = ({
    children,
    isDesktop,
    isMobile,
    areDisplayAdsEnabled,
    isTablet,
    clientConf,
    localeOrigin,
    originalUrl,
    isSearchQueryApplied,
    locale,
    isBot,
    isSsrOnlyBot,
    isTradeQuickFiltersVariant,
    isPdpMwLayoutShiftFixVariant,
    isTableSizeFilterVariant,
    urgencySignalsMwVariant,
    isContentVisibilitySbVariant,
    isPdpRespPreviewPage,
    isBannerAdsAbTestVariant,
    isShipsFromSBTileVariant,
    pdpMwUxRedesignVariant,
    isItemTileRedesignTestVariant,
    isPdpSellerReviewsVariant,
    isPdpEstimateSimilarSoldTestVariant,
    isPersonalizedCollectionTestVariant,
    isItemFavoritingVariant,
    isItemFavoritingFeb2026Variant,
    isItemFavoritingFeb2026Control,
    isHolidayGiftCollectionPage,
    isPdpBottomNewSlModuleTestVariant,
}) => {
    return (
        <ServerVarsContext.Provider
            value={{
                isDesktop,
                isMobile,
                areDisplayAdsEnabled,
                isTablet,
                clientConf,
                localeOrigin,
                originalUrl,
                isSearchQueryApplied,
                locale,
                isBot,
                isSsrOnlyBot,
                isTradeQuickFiltersVariant,
                isPdpMwLayoutShiftFixVariant,
                isTableSizeFilterVariant,
                urgencySignalsMwVariant,
                isContentVisibilitySbVariant,
                isPdpRespPreviewPage,
                isBannerAdsAbTestVariant,
                isShipsFromSBTileVariant,
                pdpMwUxRedesignVariant,
                isItemTileRedesignTestVariant,
                isPdpSellerReviewsVariant,
                isPdpEstimateSimilarSoldTestVariant,
                isPersonalizedCollectionTestVariant,
                isItemFavoritingVariant,
                isItemFavoritingFeb2026Variant,
                isItemFavoritingFeb2026Control,
                isHolidayGiftCollectionPage,
                isPdpBottomNewSlModuleTestVariant,
            }}
        >
            {children}
        </ServerVarsContext.Provider>
    );
};
